'use client';

import { useEffect, useRef, useState } from 'react';
import { theme, sectionLabel } from '../theme';
import { Container, SectionLabel, BrowserFrame } from '../primitives';
import { Reveal } from '../reveal';
import type { Strings } from '../strings';

// EditableField -clickable text that turns into an input on click.
function EditableField({
  value,
  onChange,
  multiline = false,
  style,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  style?: React.CSSProperties;
  ariaLabel?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      const range = document.createRange();
      range.selectNodeContents(ref.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [editing]);

  const editColor = theme.c.accent;
  const indicator: React.CSSProperties = editing
    ? { boxShadow: `inset 0 0 0 1.5px ${editColor}`, background: 'rgba(255,255,255,0.0)' }
    : hover
    ? { boxShadow: `inset 0 0 0 1px ${editColor}`, background: 'rgba(0,0,0,0.02)' }
    : {};

  return (
    <span
      ref={ref}
      contentEditable={editing}
      suppressContentEditableWarning
      role="textbox"
      aria-label={ariaLabel}
      onClick={() => !editing && setEditing(true)}
      onBlur={(e) => {
        setEditing(false);
        onChange(e.currentTarget.textContent || '');
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onKeyDown={(e) => {
        if (!multiline && e.key === 'Enter') {
          e.preventDefault();
          (e.currentTarget as HTMLElement).blur();
        }
        if (e.key === 'Escape') (e.currentTarget as HTMLElement).blur();
      }}
      style={{
        cursor: editing ? 'text' : 'pointer',
        outline: 'none',
        borderRadius: 4,
        padding: '1px 4px',
        margin: '-1px -4px',
        transition: 'background .12s, box-shadow .12s',
        display: 'inline-block',
        ...indicator,
        ...style,
      }}
    >
      {value}
    </span>
  );
}

export function ClickEditDemo({ s }: { s: Strings }) {
  const [brand, setBrand] = useState('Maria · Padaria');
  const [since, setSince] = useState('since 1962');
  const [headline, setHeadline] = useState(s.edit.defaultHeadline);
  const [tagline, setTagline] = useState(s.edit.defaultTagline);
  const [cta, setCta] = useState(s.edit.defaultCta);
  const [menu, setMenu] = useState(s.edit.menu);

  return (
    <section
      id="edit"
      style={{
        padding: '120px 0',
        borderTop: `1px solid ${theme.c.border}`,
      }}
    >
      <Container>
        <Reveal
          className="lp-edit-head"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: 48,
            marginBottom: 56,
          }}
        >
          <SectionLabel>{sectionLabel(s.edit.label)}</SectionLabel>
          <div>
            <h2
              style={{
                fontFamily: theme.fonts.display,
                fontSize: 'clamp(36px, 4.5vw, 64px)',
                fontWeight: 600,
                letterSpacing: -1.5,
                lineHeight: 1.02,
                color: theme.c.fg,
                margin: 0,
                maxWidth: 800,
              }}
            >
              {s.edit.title}
            </h2>
            <p
              style={{
                marginTop: 20,
                marginBottom: 0,
                fontFamily: theme.fonts.body,
                fontSize: 17,
                lineHeight: 1.55,
                color: theme.c.fgMuted,
                maxWidth: 640,
              }}
            >
              {s.edit.sub}
            </p>
          </div>
        </Reveal>

        <Reveal
          delay={120}
          className="lp-edit-body"
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 32,
            alignItems: 'start',
          }}
        >
          <div>
            <BrowserFrame url="maria-padaria.pt">
              <div
                style={{
                  background: '#F1E3CC',
                  color: '#3A2614',
                  padding: '36px 36px 36px',
                  minHeight: 360,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 12,
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    marginBottom: 36,
                    fontFamily: theme.fonts.body,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    <EditableField value={brand} onChange={setBrand} ariaLabel="brand" />
                  </span>
                  <span style={{ opacity: 0.5 }}>
                    <EditableField value={since} onChange={setSince} ariaLabel="since" />
                  </span>
                </div>

                <h3
                  style={{
                    fontFamily: theme.fonts.serif,
                    fontSize: 56,
                    lineHeight: 1,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    margin: 0,
                    marginBottom: 16,
                  }}
                >
                  <EditableField value={headline} onChange={setHeadline} ariaLabel="headline" />
                </h3>

                <div
                  style={{
                    fontFamily: theme.fonts.body,
                    fontSize: 14,
                    opacity: 0.7,
                    marginBottom: 28,
                  }}
                >
                  <EditableField value={tagline} onChange={setTagline} ariaLabel="tagline" />
                </div>

                <div
                  style={{
                    marginTop: 'auto',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    fontSize: 13,
                    fontFamily: theme.fonts.body,
                  }}
                >
                  <span
                    style={{
                      padding: '10px 16px',
                      background: '#C97B3D',
                      color: '#F1E3CC',
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                  >
                    <EditableField value={cta} onChange={setCta} ariaLabel="cta" />
                  </span>
                  <span
                    style={{
                      padding: '10px 16px',
                      border: '1px solid #3A261433',
                      borderRadius: 8,
                    }}
                  >
                    <EditableField value={menu} onChange={setMenu} ariaLabel="menu" />
                  </span>
                </div>
              </div>
            </BrowserFrame>

            <div
              style={{
                marginTop: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontFamily: theme.fonts.mono,
                fontSize: 11,
                letterSpacing: 1.2,
                color: theme.c.fgFaint,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: theme.c.accent,
                  animation: 'lpPulse 1.6s ease-in-out infinite',
                }}
              />
              <span style={{ color: theme.c.accent, fontWeight: 600 }}>
                {s.edit.livePreview}
              </span>
              <span>{s.edit.liveCaption}</span>
            </div>
          </div>

          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {s.edit.features.map((f, i) => (
              <li
                key={i}
                className="lp-card"
                style={{
                  background: theme.c.surface,
                  border: `1px solid ${theme.c.border}`,
                  borderRadius: theme.radii.md,
                  padding: '20px 22px',
                }}
              >
                <div
                  style={{
                    fontFamily: theme.fonts.display,
                    fontSize: 18,
                    fontWeight: 600,
                    letterSpacing: -0.3,
                    color: theme.c.fg,
                    marginBottom: 6,
                  }}
                >
                  {f.k}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontFamily: theme.fonts.body,
                    fontSize: 14,
                    lineHeight: 1.5,
                    color: theme.c.fgMuted,
                  }}
                >
                  {f.d}
                </p>
              </li>
            ))}
          </ul>
        </Reveal>
      </Container>

      <style>{`
        @keyframes lpPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.85); }
        }
        @media (max-width: 960px) {
          .lp-edit-head { grid-template-columns: 1fr !important; gap: 16px !important; }
          .lp-edit-body { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
